import { errorMessage } from "../../utils/mutationResult.js";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { cloneFiles } from "./fileSlice.js"
import { fetchOrQueue } from "../../lib/offlineQueue.js"
import { getCachedData, putCachedData, putOutboxItem, getAllOutboxItems } from "../../lib/offlineDb.js"
import { upsertOutboxItem } from "./offlineSlice.js"
import { siteSyncDetails } from "../../utils/siteSyncDetails.js"
import { mergeSiteData } from "../../utils/mergeSiteData.js"

function makeSiteClientId() {
  return `site_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
}

// Sites created while offline don't have a real Mongo `_id` yet, so we hand
// out a stable placeholder id built from the client id. Anything created
// against this site later (e.g. a report) can reference it by this id, and
// `syncManager` swaps every occurrence of it for the real id once the site
// itself finishes syncing.
export const buildOfflineSiteId = (clientId) => `offline-site-${clientId}`
export const isOfflineSiteId = (id) => typeof id === "string" && id.startsWith("offline-site-")

// Initial state
const initialState = {
  siteData: [],
  currentSiteData: null,
  nearbySites: [],
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  },
  filters: {
    projectId: "",
    siteType: "",
    status: "",
  },
}

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8005/api"

const getActiveReportId = () =>
  window.sessionStorage?.getItem("activeReportId") ||
  window.localStorage?.getItem("activeReportId") ||
  ""

// The outbox is the durable source of locally created sites. Overlay it on
// every list read so a reload or a server refresh cannot hide unsynced sites.
async function withPendingSites(snapshot, params, getState, queuedItems) {
  const [queued, savedIds] = await Promise.all([
    Array.isArray(queuedItems) ? queuedItems : getAllOutboxItems(),
    getCachedData("sync:resolved-ids").catch(() => null),
  ])
  const state = getState()
  const owner = state.auth?.user?._id || state.auth?.user?.id || params.userId
  const resolved = { ...savedIds, ...state.offline?.resolvedIds }
  const currentSites = state.siteData?.siteData || []
  const knownReal = new Map(currentSites.filter(site => !isOfflineSiteId(site._id))
    .flatMap(site => [[site._id, site], ...(site._clientId ? [[buildOfflineSiteId(site._clientId), site]] : [])]))
  const resolve = id => {
    const seen = new Set()
    while (resolved[id] && !seen.has(id)) { seen.add(id); id = resolved[id] }
    return id
  }
  const sites = new Map()
  for (const site of snapshot?.siteData || []) {
    const id = resolve(site._id)
    const canonical = knownReal.get(id) || knownReal.get(site._id)
    const normalized = isOfflineSiteId(site._id) && canonical
      ? { ...site, ...canonical } : { ...canonical, ...site, _id: id }
    sites.set(normalized._id, normalized)
  }
  const pending = []
  for (const item of queued || []) {
    if (item.type !== "site-create" || !item.clientId) continue
    const data = item.siteDataInfo || {}
    const itemOwner = item.meta?.userId || data.userId
    if (owner && itemOwner && String(owner) !== String(itemOwner)) continue
    if (["projectId", "siteType", "status"].some(key =>
      params[key] && params[key] !== "all" && String(params[key]) !== String(data[key] || ""))) continue
    if (params.isActive === false || params.isActive === "false") continue
    const temporaryId = buildOfflineSiteId(item.clientId)
    const id = resolve(temporaryId)
    const existing = sites.get(id) || knownReal.get(id) || knownReal.get(temporaryId)
    const site = { ...data, ...(existing || {}), _id: existing?._id || id,
      _clientId: item.clientId, _offlineQueued: isOfflineSiteId(existing?._id || id) }
    sites.delete(temporaryId)
    sites.delete(site._id)
    pending.push(site)
  }
  const siteData = [...new Map([...pending, ...sites.values()].map(site => [site._id, site])).values()]
  const added = siteData.length - (snapshot?.siteData?.length || 0)
  return { ...snapshot, siteData, pagination: {
    ...(snapshot?.pagination || initialState.pagination),
    totalItems: Math.max(siteData.length, Number(snapshot?.pagination?.totalItems || 0) + added),
  } }
}

// Restore local entities during application bootstrap as well as list reads.
// Startup already has the durable queue, so no second outbox read is needed.
/** @type {import("@reduxjs/toolkit").AsyncThunk<any, Array<any> | void, {}>} */
export const hydratePendingSites = createAsyncThunk("siteData/hydratePendingSites", async (items, { getState }) => {
  const owner = getState().auth?.user?._id || getState().auth?.user?.id
  if (!owner || getState().auth?.isAuthenticated === false) return []
  const snapshot = await withPendingSites({}, { userId: owner }, getState, items)
  const currentOwner = getState().auth?.user?._id || getState().auth?.user?.id
  return currentOwner === owner && getState().auth?.isAuthenticated !== false ? snapshot.siteData : []
})

// Async thunks for site data
/** @type {import("@reduxjs/toolkit").AsyncThunk<any, Record<string, unknown> | void, {}>} */
export const fetchSiteData = createAsyncThunk("siteData/fetchSiteData", async (params = {}, { rejectWithValue, getState }) => {
  const cacheKey = `site-data:${JSON.stringify(params)}`
  try {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      const cached = await getCachedData(cacheKey)
      return await withPendingSites(cached || {}, params, getState)
    }
    const queryParams = new URLSearchParams(params).toString()
    const response = await fetch(`${API_BASE_URL}/site-data?${queryParams}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      credentials:"include"
    })
    if (!response.ok) {
      const error = await response.json()
      return rejectWithValue(errorMessage(error, "Failed to fetch site data"))
    }

    const data = await response.json()
    await putCachedData(cacheKey, data.data)
    return await withPendingSites(data.data, params, getState)
  } catch (error) {
    const cached = await getCachedData(cacheKey).catch(() => null)
    if (cached) return withPendingSites(cached, params, getState)
    const local = await withPendingSites({}, params, getState).catch(() => null)
    if (local?.siteData.length) return local
    return rejectWithValue(errorMessage(error, "Network error"))
  }
})

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, string, {}>} */
export const fetchSiteDataById = createAsyncThunk(
  "siteData/fetchSiteDataById",
  async (siteDataId, { rejectWithValue }) => {
    try {
      const reportId = getActiveReportId()
      const params = new URLSearchParams({ populate: "true" })
      if (reportId) params.append("reportId", reportId)
      const response = await fetch(`${API_BASE_URL}/site-data/${siteDataId}?${params.toString()}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          
        },
        credentials:"include"
      })

      if (!response.ok) {
        const error = await response.json()
        return rejectWithValue(errorMessage(error, "Failed to fetch site data"))
      }

      const data = await response.json()
      return data.data
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Network error"))
    }
  },
)

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, object, {}>} */
export const createSiteData = createAsyncThunk("siteData/createSiteData", async (siteDataInfo, { rejectWithValue, dispatch, getState }) => {
  const knownOffline = typeof navigator !== "undefined" && navigator.onLine === false

  if (!knownOffline) {
    try {
      const response = await fetch(`${API_BASE_URL}/site-data`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        credentials: "include",
        body: JSON.stringify(siteDataInfo),
      })

      if (!response.ok) {
        const error = await response.json()
        return rejectWithValue(errorMessage(error, "Failed to create site data"))
      }

      const data = await response.json()
      return data.data
    } catch (error) {
      if (error?.name !== "TypeError") {
        return rejectWithValue(errorMessage(error, "Network error"))
      }
      // fall through to offline queueing below
    }
  }

  // Offline (or the fetch above failed to reach the network at all): queue
  // the creation and hand back an optimistic placeholder site so the
  // auditor can keep working — picking this site for a new report,
  // navigating into it, etc. — before it has synced.
  const clientId = makeSiteClientId()
  const userId = getState()?.auth?.user?._id || getState()?.auth?.user?.id || siteDataInfo?.userId
  const item = await putOutboxItem({
    id: `outbox_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    type: "site-create",
    siteDataInfo,
    clientId,
    meta: { description: `Create site ${siteDataInfo?.siteName || siteDataInfo?.name || ""}`, entityType: "site", entityId: clientId, ...(userId ? { userId } : {}) },
  })
  if (dispatch) dispatch(upsertOutboxItem(item))

  const placeholder = {
    ...siteDataInfo,
    _id: buildOfflineSiteId(clientId),
    _clientId: clientId,
    _offlineQueued: true,
  }
  if (dispatch) dispatch({ type: "siteData/addOptimisticSite", payload: placeholder })
  return placeholder
})

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, { siteDataId: string; siteDataInfo: object }, {}>} */
export const updateSiteData = createAsyncThunk(
  "siteData/updateSiteData",
  async ({ siteDataId, siteDataInfo }, { rejectWithValue, dispatch, getState }) => {
    try {
      const reportId = siteDataInfo?.reportId || getActiveReportId()
      const reportData =
        getState()?.auditReports?.allReportsData?.[reportId] ||
        getState()?.auditReports?.currentReportData
      const pendingSectionFiles = (reportData?.files || []).filter(
        (file) =>
          file?.isImported &&
          file?.sourceFileId &&
          file?.metadata?.importScope === "section" &&
          file?.relatedTo?.entityType !== "work" &&
          file?.relatedTo?.entityType !== "observation" &&
          !String(file?.relatedTo?.entityId || "").startsWith("imported-"),
      )
      const cloneFilesPromise =
        pendingSectionFiles.length > 0
          ? dispatch(
              cloneFiles({
                files: pendingSectionFiles,
                siteId: siteDataInfo?.siteId || siteDataId,
                reportId,
              }),
            ).unwrap()
          : Promise.resolve([])

      const requestBody = JSON.stringify({ ...siteDataInfo, ...(reportId ? { reportId } : {}) })
      const siteDetails = siteSyncDetails({ meta: { entityType: "site-data", entityId: siteDataId, reportId }, body: requestBody }, getState())
      const requestUrl = `${API_BASE_URL}/site-data/${siteDataId}`
      const requestHeaders = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      }

      // Phase 2: if the device is offline, queue this save in the outbox
      // instead of failing — the auditor's edits are kept and applied
      // optimistically, and the request is replayed once reconnected.
      const [outcome] = await Promise.all([
        fetchOrQueue({
          dispatch,
          requestInit: { url: requestUrl, method: "PUT", headers: requestHeaders, body: requestBody },
          meta: { description: siteDetails.detail, siteName: siteDetails.siteName, siteId: siteDataInfo?.siteId || siteDataId,
            reportId, entityType: "site-data", entityId: siteDataId },
          performRequest: async () => {
            const response = await fetch(requestUrl, {
              method: "PUT",
              headers: requestHeaders,
              credentials: "include",
              body: requestBody,
            })
            if (!response.ok) {
              const error = await response.json()
              const err = new Error(errorMessage(error, "Failed to update site data"))
              err.status = response.status
              throw err
            }
            return response.json()
          },
        }),
        cloneFilesPromise,
      ])

      if (outcome.queued) {
        // Reducers merge this patch into their latest state. Returning an old
        // snapshot here could undo another section save finishing concurrently.
        return { ...siteDataInfo, _id: siteDataId, reportId, _offlineQueued: true }
      }

      const data = outcome.result
      const normalizedPayload = data?.data?.data || data?.data || data
      return normalizedPayload
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Network error"))
    }
  },
)

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, string, {}>} */
export const deleteSiteData = createAsyncThunk("siteData/deleteSiteData", async (siteDataId, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_BASE_URL}/site-data/${siteDataId}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    })

    if (!response.ok) {
      const error = await response.json()
      return rejectWithValue(errorMessage(error, "Failed to delete site data"))
    }

    return siteDataId
  } catch (error) {
    return rejectWithValue(errorMessage(error, "Network error"))
  }
})

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, object, {}>} */
export const addInspectionRecord = createAsyncThunk(
  "siteData/addInspectionRecord",
  async ({ siteDataId, inspectionData }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/site-data/${siteDataId}/inspection`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(inspectionData),
      })

      if (!response.ok) {
        const error = await response.json()
        return rejectWithValue(errorMessage(error, "Failed to add inspection record"))
      }

      const data = await response.json()
      return data.data
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Network error"))
    }
  },
)

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, object, {}>} */
export const fetchNearbySites = createAsyncThunk(
  "siteData/fetchNearbySites",
  async ({ latitude, longitude, radius }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/site-data/nearby/${latitude}/${longitude}/${radius}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })

      if (!response.ok) {
        const error = await response.json()
        return rejectWithValue(errorMessage(error, "Failed to fetch nearby sites"))
      }

      const data = await response.json()
      return data.data
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Network error"))
    }
  },
)

// Site data slice
const siteDataSlice = createSlice({
  name: "siteData",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setCurrentSiteData: (state, action) => {
      state.currentSiteData = action.payload
    },
    clearCurrentSiteData: (state) => {
      state.currentSiteData = null
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    clearFilters: (state) => {
      state.filters = {
        projectId: "",
        siteType: "",
        status: "",
      }
    },
    clearNearbySites: (state) => {
      state.nearbySites = []
    },
    setSiteDataFromVersion: (state, action) => {
      // Used when loading historical version data
      state.currentSiteData = action.payload
    },
    // Reconciliation: replace the optimistic offline placeholder (matched by
    // `_clientId`) with the real, server-assigned site once `syncManager`
    // has successfully replayed the queued `site-create` outbox item.
    addOptimisticSite: (state, action) => {
      const site = action.payload
      if (!site?._id) return
      state.siteData = [site, ...state.siteData.filter((item) => item._id !== site._id)]
      state.currentSiteData = site
    },
    addSyncedSite: (state, action) => {
      const { site, clientId } = action.payload || {}
      if (!site) return
      // Keep `_clientId` on the reconciled record so any component (e.g.
      // useResolvedAuditRouteIds) that still needs to match the old
      // offline placeholder id against this now-synced site can do so on
      // the very same render the sync completes, not only after a remount.
      const syncedSite = clientId ? { ...site, _clientId: clientId } : site
      const index = state.siteData.findIndex((s) => s._clientId === clientId)
      if (index !== -1) {
        state.siteData[index] = syncedSite
      } else {
        state.siteData.unshift(syncedSite)
      }
      if (state.currentSiteData?._clientId === clientId) {
        state.currentSiteData = syncedSite
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(hydratePendingSites.fulfilled, (state, action) => {
        let added = 0
        for (const site of action.payload || []) {
          const index = state.siteData.findIndex(existing => existing._id === site._id ||
            (site._clientId && existing._clientId === site._clientId))
          if (index < 0) { state.siteData.unshift(site); added += 1 }
          else if (!isOfflineSiteId(state.siteData[index]._id) && isOfflineSiteId(site._id)) {
            // Sync may have assigned a server ID while hydration was reading.
            state.siteData[index]._clientId = site._clientId
          } else state.siteData[index] = { ...state.siteData[index], ...site }
        }
        if (added) state.pagination.totalItems = Math.max(state.siteData.length, (state.pagination.totalItems || 0) + added)
      })
      // Fetch site data cases
      .addCase(fetchSiteData.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchSiteData.fulfilled, (state, action) => {
        state.loading = false
        state.siteData = action.payload.siteData
        state.pagination = action.payload.pagination
        state.error = null
      })
      .addCase(fetchSiteData.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Fetch site data by ID cases
      .addCase(fetchSiteDataById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchSiteDataById.fulfilled, (state, action) => {
        state.loading = false
        state.currentSiteData = action.payload
        state.error = null
      })
      .addCase(fetchSiteDataById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Create site data cases
      .addCase(createSiteData.pending, (state) => {
        state.loading = true
        state.error = null
      })
  .addCase(createSiteData.fulfilled, (state, action) => {
  state.loading = false
  const site = action.payload
  const identity = site?._clientId || site?._id || site?.id
  const existingIndex = state.siteData.findIndex((item) =>
    (identity && (item?._clientId === identity || item?._id === identity || item?.id === identity)) ||
    (site?._clientId && item?._clientId === site._clientId),
  )
  if (existingIndex >= 0) state.siteData[existingIndex] = { ...state.siteData[existingIndex], ...site }
  else state.siteData.unshift(site)
  state.currentSiteData = site
  state.error = null
  })
      .addCase(createSiteData.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Update site data cases
      .addCase(updateSiteData.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateSiteData.fulfilled, (state, action) => {
        state.loading = false
        const payload = action.payload
        if (payload && payload._id) {
          const index = state.siteData.findIndex((s) => s._id === payload._id)
          if (index !== -1) {
            state.siteData[index] = mergeSiteData(state.siteData[index], payload)
          }
          if (state.currentSiteData && state.currentSiteData._id === payload._id) {
            state.currentSiteData = mergeSiteData(state.currentSiteData, payload)
          }
        } else if (payload) {
          state.currentSiteData = payload
        }
        state.error = null
      })
      .addCase(updateSiteData.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Delete site data cases
      .addCase(deleteSiteData.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteSiteData.fulfilled, (state, action) => {
        state.loading = false
        state.siteData = state.siteData.filter((s) => s._id !== action.payload)
        if (state.currentSiteData && state.currentSiteData._id === action.payload) {
          state.currentSiteData = null
        }
        state.error = null
      })
      .addCase(deleteSiteData.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Add inspection record cases
      .addCase(addInspectionRecord.fulfilled, (state, action) => {
        if (state.currentSiteData && state.currentSiteData._id === action.payload._id) {
          state.currentSiteData = action.payload
        }
        const index = state.siteData.findIndex((s) => s._id === action.payload._id)
        if (index !== -1) {
          state.siteData[index] = action.payload
        }
      })

      // Fetch nearby sites cases
      .addCase(fetchNearbySites.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchNearbySites.fulfilled, (state, action) => {
        state.loading = false
        state.nearbySites = action.payload
        state.error = null
      })
      .addCase(fetchNearbySites.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  },
})

export const { clearError, setCurrentSiteData, clearCurrentSiteData, setFilters, clearFilters, clearNearbySites, setSiteDataFromVersion, addOptimisticSite, addSyncedSite } =
  siteDataSlice.actions

export default siteDataSlice.reducer
